type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

export class HttpError extends Error {
  status: number
  data: unknown
  constructor(message: string, status: number, data: unknown) {
    super(message)
    this.name = 'HttpError'
    this.status = status
    this.data = data
  }
}

import { csrfToken } from './csrf'

function getCsrfToken(): string | null {
  return csrfToken()
}

async function request<T>(
  url: string,
  options: RequestInit & { method?: HttpMethod } = {}
): Promise<T> {
  const headers = new Headers(options.headers)
  const isFormData = options.body instanceof FormData
  headers.set('Accept', 'application/json')

  // Only set JSON content-type if we have a body to send
  const hasBody = options.body != null
  if (hasBody && !headers.has('Content-Type') && !isFormData) {
    headers.set('Content-Type', 'application/json')
  }

  const csrf = getCsrfToken()
  if (csrf) headers.set('X-CSRF-Token', csrf)

  const res = await fetch(url, {
    credentials: 'same-origin',
    ...options,
    headers,
  })

  const contentType = res.headers.get('content-type') || ''
  const isJson = contentType.includes('application/json')

  let data: unknown = null
  if (isJson) {
    data = await res.json().catch(() => null)
  } else {
    data = await res.text().catch(() => null)
  }

  if (!res.ok) {
    const message =
      isJson && typeof data === 'object' && data && 'message' in data
        ? (data.message as string)
        : typeof data === 'string' && data
        ? data
        : `Request failed with status ${res.status}`
    throw new HttpError(message, res.status, data)
  }

  return data as T
}

export const api = {
  get: <T>(url: string, init?: RequestInit) => request<T>(url, { ...init, method: 'GET' }),
  post: <T, B = unknown>(url: string, body?: B, init?: RequestInit) =>
    request<T>(url, {
      ...init,
      method: 'POST',
      body:
        body instanceof FormData
          ? (body as any)
          : body != null
          ? JSON.stringify(body)
          : undefined,
    }),
  put: <T, B = unknown>(url: string, body?: B, init?: RequestInit) =>
    request<T>(url, {
      ...init,
      method: 'PUT',
      body:
        body instanceof FormData
          ? (body as any)
          : body != null
          ? JSON.stringify(body)
          : undefined,
    }),
  patch: <T, B = unknown>(url: string, body?: B, init?: RequestInit) =>
    request<T>(url, {
      ...init,
      method: 'PATCH',
      body:
        body instanceof FormData
          ? (body as any)
          : body != null
          ? JSON.stringify(body)
          : undefined,
    }),
  delete: <T>(url: string, init?: RequestInit) => request<T>(url, { ...init, method: 'DELETE' }),
}
